/**
 * How a Facebook/Instagram post is published.
 *
 * - feed:  images (carousel) or a video, in the feed's aspect range
 * - reel:  a single portrait video (Instagram treats any video post as a Reel;
 *          Facebook needs faceBookOptions.reels)
 * - story: a single image or video, full screen, gone after 24 h
 */
export type SocialPostFormat = 'feed' | 'reel' | 'story';

export const SOCIAL_POST_FORMATS: { id: SocialPostFormat; label: string; hint: string }[] = [
  { id: 'feed', label: 'Feed', hint: 'Stays on your profile' },
  { id: 'reel', label: 'Reel', hint: 'A short portrait video' },
  { id: 'story', label: 'Story', hint: 'Full screen, gone in 24 h' },
];

/** Output aspect (width / height) the composer bakes images to, per format. */
export const FORMAT_IMAGE_ASPECT: Record<SocialPostFormat, number> = {
  feed: 4 / 5,
  reel: 9 / 16,
  story: 9 / 16,
};

export type SocialMediaKind = 'image' | 'video';

export interface SocialMediaCheck {
  kind: SocialMediaKind;
  /** width / height; null when dimensions are unknown. */
  aspect: number | null;
  durationSeconds: number | null;
}

export interface SocialFormatSpec {
  label: string;
  /** Media kinds the format accepts. */
  kinds: SocialMediaKind[];
  /** Carousel of several items, or exactly one. */
  multiple: boolean;
  /** Aspect window Meta accepts without cropping/rejecting (images and video). */
  aspectMin: number;
  aspectMax: number;
  /** Recommended aspect — what we bake images to and what we nudge videos toward. */
  aspectIdeal: number;
  durationMin: number | null;
  durationMax: number | null;
  /** Caption text is dropped by the network (stories). */
  captionIgnored: boolean;
}

/**
 * Per-network publishing rules. Numbers come from Meta's media specs as
 * documented by Ayrshare (Instagram feed 4:5–1.91:1, Reels 9:16 recommended
 * with 0.01–10 accepted, Stories 9:16, video stories ≤ 60 s, Reels ≥ 3 s).
 */
export const SOCIAL_FORMAT_SPECS: Record<'instagram' | 'facebook', Record<SocialPostFormat, SocialFormatSpec>> = {
  instagram: {
    feed: {
      label: 'Instagram feed',
      kinds: ['image', 'video'],
      multiple: true,
      aspectMin: 4 / 5,
      aspectMax: 1.91,
      aspectIdeal: 4 / 5,
      durationMin: 3,
      durationMax: 60,
      captionIgnored: false,
    },
    reel: {
      label: 'Instagram Reel',
      kinds: ['video'],
      multiple: false,
      aspectMin: 0.01,
      aspectMax: 10,
      aspectIdeal: 9 / 16,
      durationMin: 3,
      durationMax: 15 * 60,
      captionIgnored: false,
    },
    story: {
      label: 'Instagram Story',
      kinds: ['image', 'video'],
      multiple: false,
      aspectMin: 0.01,
      aspectMax: 10,
      aspectIdeal: 9 / 16,
      durationMin: 3,
      durationMax: 60,
      captionIgnored: true,
    },
  },
  facebook: {
    feed: {
      label: 'Facebook feed',
      kinds: ['image', 'video'],
      multiple: true,
      aspectMin: 0.01,
      aspectMax: 10,
      aspectIdeal: 4 / 5,
      durationMin: null,
      durationMax: 20 * 60,
      captionIgnored: false,
    },
    reel: {
      label: 'Facebook Reel',
      kinds: ['video'],
      multiple: false,
      aspectMin: 0.01,
      aspectMax: 10,
      aspectIdeal: 9 / 16,
      durationMin: 3,
      durationMax: 90,
      captionIgnored: false,
    },
    story: {
      label: 'Facebook Story',
      kinds: ['image', 'video'],
      multiple: false,
      aspectMin: 0.01,
      aspectMax: 10,
      aspectIdeal: 9 / 16,
      durationMin: null,
      durationMax: 60,
      captionIgnored: true,
    },
  },
};

const ASPECT_LABELS: [number, string][] = [
  [9 / 16, '9:16'],
  [4 / 5, '4:5'],
  [1, '1:1'],
  [1.91, '1.91:1'],
];

/** "9:16" / "4:5" / "1.4:1" — nearest named ratio, else a decimal. */
export function formatAspect(aspect: number | null): string {
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0) return 'unknown';
  const named = ASPECT_LABELS.find(([value]) => Math.abs(aspect - value) / value < 0.04);
  if (named) return named[1];
  return `${aspect.toFixed(2)}:1`;
}

export function formatSeconds(seconds: number | null): string {
  if (!seconds || !Number.isFinite(seconds)) return '';
  if (seconds < 90) return `${Math.round(seconds)}s`;
  return `${Math.round(seconds / 60)} min`;
}

export interface SocialFormatProblem {
  level: 'error' | 'warn';
  message: string;
}

/**
 * Check media against a format's rules.
 * Errors block publishing (the network would reject the post); warnings are
 * advisory (the post goes through but gets cropped or letterboxed).
 */
export function checkSocialFormat(
  platform: 'instagram' | 'facebook',
  format: SocialPostFormat,
  items: SocialMediaCheck[],
): SocialFormatProblem[] {
  const spec = SOCIAL_FORMAT_SPECS[platform][format];
  const problems: SocialFormatProblem[] = [];
  if (items.length === 0) return problems;

  if (!spec.multiple && items.length > 1) {
    problems.push({ level: 'error', message: `${spec.label} takes a single photo or video.` });
  }

  const videos = items.filter((i) => i.kind === 'video');
  const images = items.filter((i) => i.kind === 'image');

  if (!spec.kinds.includes('video') && videos.length > 0) {
    problems.push({ level: 'error', message: `${spec.label} doesn't support video.` });
  }
  if (!spec.kinds.includes('image') && images.length > 0) {
    problems.push({
      level: 'error',
      message: `${spec.label} needs a video${images.length ? ' — photos can\'t be posted as a Reel' : ''}.`,
    });
  }
  if (videos.length > 0 && images.length > 0) {
    problems.push({
      level: 'error',
      message: 'A post can\'t mix photos and video — use all photos or one video.',
    });
  }
  if (videos.length > 1) {
    problems.push({ level: 'error', message: 'Only one video per post.' });
  }

  for (const item of items) {
    if (item.kind === 'video' && item.durationSeconds != null) {
      if (spec.durationMin != null && item.durationSeconds < spec.durationMin) {
        problems.push({
          level: 'error',
          message: `${spec.label} videos must be at least ${formatSeconds(spec.durationMin)} (this one is ${formatSeconds(item.durationSeconds)}).`,
        });
      }
      if (spec.durationMax != null && item.durationSeconds > spec.durationMax) {
        problems.push({
          level: 'error',
          message: `${spec.label} videos can be at most ${formatSeconds(spec.durationMax)} (this one is ${formatSeconds(item.durationSeconds)}).`,
        });
      }
    }
    if (item.aspect != null && (item.aspect < spec.aspectMin || item.aspect > spec.aspectMax)) {
      problems.push({
        level: 'warn',
        message: `This ${item.kind} is ${formatAspect(item.aspect)} — ${spec.label} displays best at ${formatAspect(spec.aspectIdeal)}, so it will be cropped.`,
      });
    }
  }

  return problems;
}
